import React, { useState, useEffect, useContext, useMemo } from "react";
import { useSearchParams, useLocation, useNavigate } from "react-router-dom";
import { performApiRequest } from "src/utils/apiRequest";
import { UserStoryContext } from "src/context/UserStoryState";
import { server1 } from "src/utils/httpsRequest";

const DynamicImport = () => {
  const { contextData, AddContextData } = useContext(UserStoryContext);
  const [activeTab, setActiveTab] = useState("vault");
  const [isSidebarExpanded, setIsSidebarExpanded] = useState(true);
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const navigate = useNavigate();
  const [UserStoryComponent, setUserStoryComponent] = useState(null);
  const [SidebarComponent, setSidebarComponent] = useState(null);
  const [dataLoaded, setDataLoaded] = useState(false);

  // Fetch user data from localStorage

  const userData = useMemo(
    () => JSON.parse(localStorage.getItem("userData")) ?? {},
    []
  );
  const {
    access_token: token,
    tenant_id: tenantId,
    app_id: Appid,
    role: roles,
    load,
  } = userData;

  // Redirect to login if token is missing
  useEffect(() => {
    if (!token) {
      navigate(
        `/login?redirect=${encodeURIComponent(
          location.pathname + location.search
        )}`
      );
    }
  }, [token, navigate, location.pathname, location.search]);

  // Compute the folder path dynamically
  const folderPath = useMemo(() => {
    const org = tenantId || "defaultOrg";
    const app = Appid || "defaultApp";
    return `UserStory/${org}/${app}/${roles}`;
  }, [tenantId, roles, Appid]);

  // Dynamically load components and fetch additional data if needed
  useEffect(() => {
    const loadComponents = async () => {
      try {
        const templateName = searchParams.get("templateName");
        const org = folderPath.split("/")[1];

        // Fetch trainee data if necessary
        if (
          (org === "64f9f31ab8f2b3a65cbfc48d" ||
            org === "64f9f31ab8f2b3acae3x4sfe") &&
          !dataLoaded &&
          (load !== false ||
            !contextData.some((data) => data.hasOwnProperty("token")))
        ) {
          await traineeAdmin(userData, AddContextData);
          setDataLoaded(true);
        }

        // Dynamically import Sidebar and UserStory components
        const { default: Sidebar } = await import(
          `src/pages/${folderPath}/Sidebar`
        );
        setSidebarComponent(() => Sidebar);

        const { default: Component } = await import(
          `src/pages/${folderPath}/${templateName ?? "Dashboard"}`
        );
        setUserStoryComponent(() => Component);
      } catch (error) {
        console.error("Error loading components", error);
      }
    };

    if (token) {
      loadComponents();
    }
  }, [
    folderPath,
    searchParams,
    token,
    contextData,
    dataLoaded,
    userData,
    AddContextData,
  ]);

  // Fallback while components are being loaded
  if (!UserStoryComponent || !SidebarComponent) {
    return <div>Loading...</div>;
  }

  return (
    <div className="flex bg-gray-100">
      <SidebarComponent
        isExpanded={isSidebarExpanded}
        setIsExpanded={setIsSidebarExpanded}
        activeTab={activeTab}
        setActiveTab={setActiveTab}
      />
      <div className="flex-1 max-h-screen overflow-y-auto">
        <UserStoryComponent activeTab={activeTab} setActiveTab={setActiveTab} />
      </div>
    </div>
  );
};

export default DynamicImport;

// Helper function to fetch trainee data
async function traineeAdmin(userData, AddContextData) {
  var userPayload;
  var response;
  if (userData.scopes === "trainee") {
    userPayload = {
      target: "trainee",
      action: "search",
      filters: {
        logicalOperator: "AND",
        rules: [
          {
            field: "email",
            dataType: "tag",
            comparisonOperator: "equals",
            value: userData?.user_id,
          },
        ],
      },
      columnsToRetrieve: [
        { field: "recordId", dataType: "tag" },
        { field: "name", dataType: "tag" },
        { field: "dateOfBirth", dataType: "tag" },
        { field: "traineeGender", dataType: "tag" },
        { field: "contactNumberPhoneNumber", dataType: "tag" },
        { field: "email", dataType: "tag" },
        { field: "qualificationAttained", dataType: "tag" },
        { field: "englishProficiency", dataType: "tag" },
        { field: "traineeStatus", dataType: "tag" },
        { field: "traineeId", dataType: "tag" },
        { field: "idType2", dataType: "tag" },
        { field: "idType1", dataType: "tag" },
        { field: "lmsEmail", dataType: "tag" },
        { field: "race", dataType: "tag" },
        { field: "contactNumberContactType", dataType: "tag" },
        { field: "nationality", dataType: "tag" },
      ],
      resultDisplayOrder: "row",
      maxRowsPerPage: 1,
      pageToRetrieve: 1,
    };
    response = await performApiRequest(
      1,
      "bexInsights",
      "/bexinsights/search",
      "post",
      userPayload
    );
  } else if (userData.scopes === "agent") {
    userPayload = {
      target: "agent",
      action: "search",
      filters: {
        logicalOperator: "AND",
        rules: [
          {
            field: "email",
            dataType: "tag",
            comparisonOperator: "equals",
            value: userData?.user_id,
          },
        ],
      },
      columnsToRetrieve: [
        // { field: "recordId", dataType: "tag" },
        // { field: "name", dataType: "tag" },
        // { field: "dateOfBirth", dataType: "tag" },
        // { field: "traineeGender", dataType: "tag" },
        // { field: "contactNumberPhoneNumber", dataType: "tag" },
        // { field: "email", dataType: "tag" },
        // { field: "qualificationAttained", dataType: "tag" },
        // { field: "englishProficiency", dataType: "tag" },
        // { field: "traineeStatus", dataType: "tag" },
        // { field: "traineeId", dataType: "tag" },
        // { field: "idType2", dataType: "tag" },
        // { field: "idType1", dataType: "tag" },
        // { field: "lmsEmail", dataType: "tag" },
        // { field: "race", dataType: "tag" },
        // { field: "contactNumberContactType", dataType: "tag" },
        // { field: "nationality", dataType: "tag" },
      ],
      resultDisplayOrder: "row",
      maxRowsPerPage: 1,
      pageToRetrieve: 1,
    };
    response = await performApiRequest(
      1,
      "bexInsights",
      "/bexinsights/v2/search",
      "post",
      userPayload
    );
  }

  try {
    // const response = await performApiRequest(1, "bexInsights", "/bexinsights/v2/search", "post", userPayload);
    const data = response?.data?.tableRow?.[0];

    if (data) {
      const mergedData = {
        ...data,
        token: userData?.access_token,
        tenant_id: userData?.tenant_id,
        email: userData?.user_id,
      };

      AddContextData(mergedData);

      const updatedUserData = { ...userData, load: false };
      localStorage.setItem("userData", JSON.stringify(updatedUserData));
    } else {
      console.warn("No data found for the trainee");
    }
  } catch (error) {
    console.error("Error during API request:", error);
  }
}
